import { Link } from 'react-router-dom';
import { Button, Card, CardContent } from '../../components/ui';
import { useLanguageStore } from '../../store/languageStore';
import { getTranslation } from '../../i18n/translations';

export const HomePage = () => {
  const { language } = useLanguageStore();
  const t = getTranslation(language);

  return (
    <div className="flex flex-col">
      {/* Hero Section with Gradient */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 px-4 py-32 md:py-48">
        <div className="container mx-auto text-center">
          <div className="mb-8 inline-block">
            <h1 className="text-6xl font-black tracking-tight md:text-7xl lg:text-8xl">
              <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">
                {t.home.hero.titleHighlight}
              </span>
            </h1>
          </div>
          <p className="mx-auto mb-6 max-w-3xl text-xl font-semibold text-foreground md:text-2xl">
            {t.home.hero.tagline}
          </p>
          <p className="mx-auto mb-12 max-w-2xl text-lg text-muted-foreground">
            {t.home.hero.subtitle}
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/apps">
              <Button size="lg" className="w-full sm:w-auto">{t.home.hero.exploreApps}</Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                {t.home.hero.learnMore}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Games Highlight Section */}
      <section className="bg-gradient-to-b from-background to-muted/50 py-24">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold md:text-5xl">{t.home.games.title}</h2>
            <p className="text-lg text-muted-foreground">{t.home.games.subtitle}</p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Moving Motivators Card */}
            <Link to="/apps/moving-motivators" className="group">
              <Card className="relative h-80 overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-2">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20" />
                <CardContent className="flex h-full flex-col justify-between p-8">
                  <div>
                    <div className="mb-4 text-6xl">💫</div>
                    <h3 className="mb-3 text-2xl font-bold">{t.home.games.motivators.title}</h3>
                  </div>
                  <p className="mb-6 text-foreground/90">
                    {t.home.games.motivators.description}
                  </p>
                  <Button className="w-full" size="sm">
                    {t.home.games.motivators.cta}
                  </Button>
                </CardContent>
              </Card>
            </Link>

            {/* Delegation Poker Card */}
            <Link to="/apps/delegation-poker" className="group">
              <Card className="relative h-80 overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-2">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20" />
                <CardContent className="flex h-full flex-col justify-between p-8">
                  <div>
                    <div className="mb-4 text-6xl">🃏</div>
                    <h3 className="mb-3 text-2xl font-bold">{t.home.games.poker.title}</h3>
                  </div>
                  <p className="mb-6 text-foreground/90">
                    {t.home.games.poker.description}
                  </p>
                  <Button className="w-full" size="sm">
                    {t.home.games.poker.cta}
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Squadron Play Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold md:text-5xl">{t.home.why.title}</h2>
            <p className="text-lg text-muted-foreground">{t.home.why.subtitle}</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <Card className="border-none bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-blue-500/20">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className="mb-3 text-xl font-semibold">{t.home.why.engaging.title}</h3>
                <p className="text-muted-foreground">{t.home.why.engaging.description}</p>
              </CardContent>
            </Card>

            <Card className="border-none bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-purple-500/20">
                  <span className="text-3xl">👥</span>
                </div>
                <h3 className="mb-3 text-xl font-semibold">{t.home.why.collaborative.title}</h3>
                <p className="text-muted-foreground">{t.home.why.collaborative.description}</p>
              </CardContent>
            </Card>

            <Card className="border-none bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-950 dark:to-pink-900">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-pink-500/20">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="mb-3 text-xl font-semibold">{t.home.why.instant.title}</h3>
                <p className="text-muted-foreground">{t.home.why.instant.description}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-gradient-to-r from-primary/90 to-purple-600/90 px-4 py-20 text-center text-white">
        <div className="container mx-auto">
          <h2 className="mb-4 text-4xl font-bold md:text-5xl">{t.home.cta.title}</h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-white/90">
            {t.home.cta.subtitle}
          </p>
          <Link to="/apps">
            <Button size="lg" variant="secondary" className="font-semibold">
              {t.home.cta.button}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
